import "./SlidingPanel.scss";

import { SbbButtonLink, SbbSecondaryButtonLink, SbbTransparentButtonLink } from "@sbb-esta/lyne-react/button";
import { SbbLoadingIndicatorCircle } from "@sbb-esta/lyne-react/loading-indicator-circle";
import { defaultButtonSize } from "components/Lyne/LyneConstants";
import { createLogger } from "logging";
import { useTranslations } from "providers";
import React, { PropsWithChildren, useEffect, useRef } from "react";
import { CSSTransition } from "react-transition-group";

const logger = createLogger("SlidingPanel");

export type PanelType = "top" | "right" | "bottom" | "left";

export interface SliderProps extends PropsWithChildren {
  type?: PanelType;
  size?: number;
  panelContainerClassName?: string;
  panelClassName?: string;
  isOpen: boolean;
  debugContent?: React.ReactElement;
  noBackdrop?: boolean;
  onOpen?: (node?: HTMLElement, isAppearing?: boolean) => void;
  onOpening?: (node?: HTMLElement, isAppearing?: boolean) => void;
  onOpened?: (node?: HTMLElement, isAppearing?: boolean) => void;
  onCancel?: () => void;
  onClose?: (node?: HTMLElement) => void;
  onClosing?: (node?: HTMLElement) => void;
  onClosed?: (node?: HTMLElement) => void;
  onSave?: () => void;
  saveButtonDisabled?: boolean;
  header?: React.ReactElement;
  footer?: React.ReactElement;
  isSubmitting?: boolean;
  buttonSize?: "s" | "m" | "l";
}

type GlassPanelStyle = {
  width: string;
  height: string;
  left?: number;
  right?: number;
  bottom?: number;
  overflow?: string;
  position:
    | "inherit"
    | "-moz-initial"
    | "initial"
    | "revert"
    | "unset"
    | "-webkit-sticky"
    | "absolute"
    | "fixed"
    | "relative"
    | "static"
    | "sticky"
    | undefined;
};

const getGlassPanelStyle = (type: PanelType, size: number, hidden: boolean): GlassPanelStyle => {
  const isHorizontal = type === "bottom" || type === "top";
  return {
    width: isHorizontal ? `${hidden ? "0" : "100"}vw` : `${100 - size}vw`,
    height: isHorizontal ? `${100 - size}vh` : `${hidden ? "0" : "100"}vh`,
    ...(type === "right" && { left: 0 }),
    ...(type === "top" && { bottom: 0 }),
    position: "inherit",
  };
};

const getPanelStyle = (type: PanelType, size: number): GlassPanelStyle => {
  const isHorizontal = type === "bottom" || type === "top";
  return {
    width: isHorizontal ? "100vw" : `${size}vw`,
    height: isHorizontal ? `${size}vh` : "100vh",
    ...(type === "right" && { right: 0 }),
    ...(type === "bottom" && { bottom: 0 }),
    position: "inherit",
    overflow: "hidden",
  };
};

interface GlassPanelProps {
  type: PanelType;
  size: number;
  noBackdrop?: boolean;
  backdropClicked?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

export const GlassPanel = ({ type, size, noBackdrop, backdropClicked }: GlassPanelProps) => {
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!noBackdrop && backdropClicked) {
      backdropClicked(e);
    }
  };
  return <div className="glass" style={getGlassPanelStyle(type, size, !!noBackdrop)} onClick={handleBackdropClick} />;
};

export const SlidingPanel: React.FC<SliderProps> = ({
  type = "right",
  buttonSize = defaultButtonSize,
  size = 35,
  panelContainerClassName,
  panelClassName,
  isOpen,
  onOpen,
  onOpening,
  onOpened,
  onClose,
  onClosing,
  onClosed,
  onCancel,
  noBackdrop = false,
  children,
  debugContent,
  onSave, // Receive the save function as a prop
  saveButtonDisabled = false,
  isSubmitting,
  header,
  footer,
}) => {
  const i18n = useTranslations();
  const nodeRef = useRef(null);

  const isHorizontal = type === "bottom" || type === "top";
  const glassBefore = type === "right" || type === "bottom";
  const effectiveType = type || "left";

  const removeBodyClass = () => document.body.classList.remove("sliding-panel-open");
  const addBodyClass = () => document.body.classList.add("sliding-panel-open");

  const [effectiveSize, setEffectiveSize] = React.useState(size);
  React.useMemo(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setEffectiveSize(100); // Set size to 100 for devices smaller than 768px
      } else if (window.innerWidth < 1150) {
        setEffectiveSize(60); // Set size to 60 for devices between 768px and 992px
      } else {
        setEffectiveSize(size); // Reset to the original size for larger devices
      }
    };
    handleResize();

    // Add event listener for resize
    window.addEventListener("resize", handleResize);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [size]);

  useEffect(() => {
    if (isOpen) addBodyClass();
    else removeBodyClass();
    return () => removeBodyClass();
  }, [isOpen]);

  function handleDismiss(e: React.SyntheticEvent) {
    e.stopPropagation();
    logger.debug(`handleDismiss(): ${panelClassName}`, e);
    removeBodyClass();
    if (onClose) onClose();
  }

  function handleCancel(e: React.SyntheticEvent) {
    e.stopPropagation();
    logger.debug(`handleCancel(): ${panelClassName}`, e);
    removeBodyClass();
    if (onCancel) onCancel();
  }

  function handleSave(e: React.SyntheticEvent) {
    e.stopPropagation();
    logger.debug(`handleSave(): ${panelClassName}`, e);
    if (onSave) onSave();
  }

  return (
    <div className="sliding-panel">
      <div className={`sliding-panel-container ${isOpen ? "active" : ""} ${noBackdrop ? "click-through" : ""}`}>
        <CSSTransition
          in={isOpen}
          timeout={500}
          classNames={`panel-container-${type}`}
          unmountOnExit
          onEnter={onOpen}
          onEntering={onOpening}
          onEntered={onOpened}
          onExit={onClose}
          onExiting={onClosing}
          onExited={onClosed}
          style={{ display: isHorizontal ? "block" : "flex" }}
          nodeRef={nodeRef}
        >
          <div ref={nodeRef}>
            {glassBefore && (
              <GlassPanel
                noBackdrop={noBackdrop}
                backdropClicked={(e) => handleDismiss(e as React.SyntheticEvent)}
                type={type}
                size={effectiveSize}
              />
            )}
            <div
              className={`panel ${panelContainerClassName || ""}`}
              style={getPanelStyle(effectiveType, effectiveSize)}
            >
              <div className="panel-header d-flex">
                {header}
                <div className="ml-auto">
                  <SbbTransparentButtonLink
                    className="cancel-button"
                    size="s"
                    iconName="cross-medium"
                    disabled={isSubmitting}
                    onClick={(e) => handleDismiss(e)}
                  />
                </div>
              </div>
              <div className={`panel-content ${panelClassName || ""}`}>
                <div className="panel-container">
                  {children}
                  {debugContent && <div className="debug-content">{debugContent}</div>}
                </div>
              </div>
              <div className="panel-footer">
                {footer ?? (
                  <div className="panel-buttons">
                    <SbbSecondaryButtonLink
                      className="cancel-button"
                      size={buttonSize}
                      iconName="cross-medium"
                      disabled={isSubmitting}
                      onClick={(e) => handleCancel(e)}
                    >
                      {i18n.Actions_Cancel}
                    </SbbSecondaryButtonLink>

                    <SbbButtonLink
                      className="save-button"
                      iconName="tick-medium"
                      disabled={saveButtonDisabled}
                      size={buttonSize}
                      onClick={(e) => handleSave(e)}
                    >
                      {i18n.Actions_Save}
                      {isSubmitting && <SbbLoadingIndicatorCircle color="smoke" />}
                    </SbbButtonLink>
                  </div>
                )}
              </div>
            </div>

            {!glassBefore && (
              <GlassPanel
                noBackdrop={noBackdrop}
                backdropClicked={(e) => handleDismiss(e as React.SyntheticEvent)}
                type={effectiveType}
                size={effectiveSize}
              />
            )}
          </div>
        </CSSTransition>
      </div>
    </div>
  );
};
